class HeaderDrawerDesktopUs extends HTMLElement {
  constructor() {
    super();

    this.BODY_LOCK_CLASS = 'overflow-hidden-desktop';
    this.DESKTOP_MQ = '(min-width: 990px)';
    this.CLICK_SUPPRESS_MS = 800;

    this.mq = window.matchMedia(this.DESKTOP_MQ);
    this.header = document.querySelector('.section-header');

    this._onCapturePointerDown = this.onCapturePointerDown.bind(this);
    this._onCaptureClick = this.onCaptureClick.bind(this);
    this._onBackdropClick = this.onBackdropClick.bind(this);
    this._onDocumentClick = this.onDocumentClick.bind(this);
    this._onEscape = this.onEscape.bind(this);
    this._onMqChange = this.onMqChange.bind(this);
    this._handleDrawerToggle = this.handleDrawerToggle.bind(this);
    this._onPointerCancel = this.onPointerCancel.bind(this);
  }

  connectedCallback() {
    this.backdrop = this.querySelector('.header-drawer-desktop-us__backdrop');

    this.querySelectorAll('details.menu-drawer-container').forEach((details) => {
      details.addEventListener('toggle', this._handleDrawerToggle);
    });

    this.addEventListener('pointerdown', this._onCapturePointerDown, true);
    this.addEventListener('pointercancel', this._onPointerCancel);
    this.addEventListener('click', this._onCaptureClick, true);
    this.backdrop?.addEventListener('click', this._onBackdropClick);
    document.addEventListener('click', this._onDocumentClick);
    document.addEventListener('keyup', this._onEscape, true);
    this.mq.addEventListener('change', this._onMqChange);
  }

  disconnectedCallback() {
    this.querySelectorAll('details.menu-drawer-container').forEach((details) => {
      details.removeEventListener('toggle', this._handleDrawerToggle);
    });

    this.removeEventListener('pointerdown', this._onCapturePointerDown, true);
    this.removeEventListener('pointercancel', this._onPointerCancel);
    this.removeEventListener('click', this._onCaptureClick, true);
    this.backdrop?.removeEventListener('click', this._onBackdropClick);
    document.removeEventListener('click', this._onDocumentClick);
    document.removeEventListener('keyup', this._onEscape, true);
    this.mq.removeEventListener('change', this._onMqChange);

    this.teardownDesktopState();
  }

  isDesktop() {
    return this.mq.matches;
  }

  reducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  getOpenDrawer() {
    return this.querySelector('details.menu-drawer-container[open]');
  }

  setBackdropVisible(visible) {
    this.classList.toggle('header-drawer-desktop-us--backdrop-visible', visible);
    this.backdrop?.classList.toggle('header-drawer-desktop-us__backdrop--visible', visible);
    this.header.classList.toggle('menu-open', visible);
    document.dispatchEvent(new CustomEvent('header:menu-open-change'));
  }

  setScrollLock(locked) {
    document.body.classList.toggle(this.BODY_LOCK_CLASS, locked);
  }

  ensureHeaderVisibleForMenu() {
    if (!this.header?.classList.contains('shopify-section-header-hidden')) return;
    this.header.classList.add('shopify-section-header-sticky', 'animate');
    this.header.classList.remove('shopify-section-header-hidden');
  }

  syncHeaderBottomPositionDesktop() {
    const headerWrapper = this.header?.querySelector('.header-wrapper');
    if (!headerWrapper) return;

    const borderOffset = this.closest('.header-wrapper').classList.contains('header-wrapper--border-bottom') ? 1 : 0;
    document.documentElement.style.setProperty(
      '--header-bottom-position',
      `${parseInt(headerWrapper.getBoundingClientRect().bottom - borderOffset)}px`
    );

    document.documentElement.style.setProperty('--header-bottom-position-desktop', `${Math.floor(headerWrapper.getBoundingClientRect().bottom)}px`);
    document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
  }

  handleDrawerToggle(event) {
    const details = event.currentTarget;
    if (!details.open) {
      details.classList.remove('menu-opening');
      delete details.dataset.menuClosing;
    }
    if (!this.querySelector('details.menu-drawer-container[open]')) {
      this.setBackdropVisible(false);
      this.setScrollLock(false);
      window.removeEventListener('resize', this.onResize);
    }
  }

  onPointerCancel() {
    this._suppressMenuSummaryClick = null;
    this._suppressMenuSummaryClickUntil = 0;
  }

  teardownDesktopState() {
    removeTrapFocus(null);
    this.querySelectorAll('details.menu-drawer-container').forEach((details) => {
      details.removeAttribute('open');
      details.classList.remove('menu-opening');
      delete details.dataset.menuClosing;
      const summary = details.querySelector('summary');
      summary?.setAttribute('aria-expanded', 'false');
    });
    this.setBackdropVisible(false);
    this.setScrollLock(false);
    this.onPointerCancel();
  }

  onMqChange(event) {
    if (!event.matches) {
      this.teardownDesktopState();
    }
  }

  panelAndClose(details) {
    const panel = details.querySelector('.menu-drawer');
    return { panel };
  }

  focusPanel(details) {
    const { panel } = this.panelAndClose(details);
    if (panel) trapFocus(panel || panel);
  }

  openDrawerAnimated(details, summary) {
    const { panel } = this.panelAndClose(details);
    if (!panel) return;

    window.addEventListener('resize', this.onResize);

    details.setAttribute('open', '');
    summary.setAttribute('aria-expanded', 'true');
    this.syncHeaderBottomPositionDesktop();
    this.setBackdropVisible(true);
    this.setScrollLock(true);

    setTimeout(() => {
      details.classList.add('menu-opening');
    }, 0);

    if (this.reducedMotion()) {
      this.focusPanel(details);
      return;
    }

    let fallbackTimer;
    const done = (event) => {
      if (event && event.propertyName && event.propertyName !== 'transform') return;
      panel.removeEventListener('transitionend', done);
      clearTimeout(fallbackTimer);
      this.focusPanel(details);
    };

    panel.addEventListener('transitionend', done);
    fallbackTimer = setTimeout(done, 200);
  }

  closeDrawerAnimated(details, summary) {
    if (!this.isDesktop()) return;
    if (!details.hasAttribute('open') || details.dataset.menuClosing === 'true') return;

    details.dataset.menuClosing = 'true';
    details.classList.remove('menu-opening');
    summary.setAttribute('aria-expanded', 'false');
    
    removeTrapFocus(summary);
    
    const finish = () => {
      details.removeAttribute('open');
      delete details.dataset.menuClosing;
    };

    if (this.reducedMotion()) {
      finish();
      return;
    }

    let animationStart;
    const handleAnimation = (time) => {
      if (animationStart === undefined) animationStart = time;
      if (time - animationStart < 200) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        finish();
      }
    };
    window.requestAnimationFrame(handleAnimation);
  }

  swapDrawer(oldDetails, newDetails, newSummary) {
    const oldSummary = oldDetails.querySelector('summary');
    const { panel: newPanel } = this.panelAndClose(newDetails);
    if (!newPanel) return;

    removeTrapFocus(null);

    newPanel.style.transition = 'none';
    newDetails.setAttribute('open', '');
    newDetails.classList.add('menu-opening');
    newSummary.setAttribute('aria-expanded', 'true');

    oldDetails.classList.remove('menu-opening');
    oldDetails.removeAttribute('open');
    oldSummary?.setAttribute('aria-expanded', 'false');

    this.syncHeaderBottomPositionDesktop();

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        newPanel.style.transition = '';
      });
    });

    this.setBackdropVisible(true);
    this.setScrollLock(true);

    trapFocus(newPanel);
  }

  menuSummaryFromEventTarget(target) {
    if (!target || !target.closest) return null;
    const summary = target.closest('summary.header__menu-item');
    if (!summary || !this.contains(summary)) return null;
    const details = summary.closest('details.menu-drawer-container');
    if (!details || !this.contains(details)) return null;
    return { summary, details };
  }

  handleMenuSummaryInteraction(summary, details, event) {
    event.preventDefault();
    event.stopImmediatePropagation();

    if (details.dataset.menuClosing === 'true') return;

    const currentlyOpen = this.getOpenDrawer();
    const isFullyOpen =
      details.hasAttribute('open') && details.classList.contains('menu-opening');

    if (currentlyOpen === details && isFullyOpen) {
      this.closeDrawerAnimated(details, summary);
      return;
    }

    this.ensureHeaderVisibleForMenu();

    if (currentlyOpen && currentlyOpen !== details) {
      this.swapDrawer(currentlyOpen, details, summary);
      return;
    }

    this.openDrawerAnimated(details, summary);
  }

  onCapturePointerDown(event) {
    if (!this.isDesktop()) return;
    if (!event.isPrimary || event.button !== 0) return;

    const hit = this.menuSummaryFromEventTarget(event.target);
    if (!hit) return;

    this._suppressMenuSummaryClick = hit.summary;
    this._suppressMenuSummaryClickUntil = performance.now() + this.CLICK_SUPPRESS_MS;

    this.handleMenuSummaryInteraction(hit.summary, hit.details, event);
  }

  onCaptureClick(event) {
    if (!this.isDesktop()) return;

    const hit = this.menuSummaryFromEventTarget(event.target);
    if (!hit) return;

    if (
      this._suppressMenuSummaryClick === hit.summary &&
      performance.now() < this._suppressMenuSummaryClickUntil
    ) {
      event.preventDefault();
      event.stopImmediatePropagation();
      this._suppressMenuSummaryClick = null;
      this._suppressMenuSummaryClickUntil = 0;
      return;
    }

    this.handleMenuSummaryInteraction(hit.summary, hit.details, event);
  }

  onBackdropClick() {
    if (!this.isDesktop()) return;
    const open = this.getOpenDrawer();
    if (open) this.closeDrawerAnimated(open, open.querySelector('summary'));
  }

  onDocumentClick(event) {
    if (!this.isDesktop()) return;
    if (event.button !== 0 && event.button !== undefined) return;

    const open = this.getOpenDrawer();
    if (!open) return;

    const clickedMenuSummary = event.target.closest(
      'details.menu-drawer-container > summary.header__menu-item'
    );
    if (clickedMenuSummary && this.contains(clickedMenuSummary)) return;

    const panel = open.querySelector('.menu-drawer');
    if (panel?.contains(event.target)) return;

    this.closeDrawerAnimated(open, open.querySelector('summary'));
  }

  onEscape(event) {
    if (!this.isDesktop()) return;
    if (event.code.toUpperCase() !== 'ESCAPE') return;

    const open = this.getOpenDrawer();
    if (!open) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    this.closeDrawerAnimated(open, open.querySelector('summary'));
  }

  onResize = () => {
    document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
  };
}

customElements.define('header-drawer-desktop-us', HeaderDrawerDesktopUs);
